import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BedDouble, Bath, Ruler } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const imgOf = (l) => {
  const im = l.image;
  if (!im) return null;
  if (typeof im === "string") return im.startsWith("//") ? `https:${im}` : im;
  if (im.url) return im.url.startsWith("//") ? `https:${im.url}` : im.url;
  const entries = Object.keys(im)
    .filter((k) => !isNaN(Number(k)))
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => im[k])
    .filter((v) => v && typeof v === "object");
  const first = entries[0];
  if (!first) return null;
  const u = first.url || first.urls?.hires?.url || first.urls?.thumb?.url || first.sizes?.thumb;
  return u ? (u.startsWith("//") ? `https:${u}` : u) : null;
};

const ListingCards = ({ kind = "featured", limit = 6, dark = false, heading, query, emptyNode }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let live = true;
    axios
      .get(`${API}/idx/${kind}`, { params: { limit } })
      .then((r) => {
        if (live) setItems(Array.isArray(r.data?.data) ? r.data.data : []);
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [kind, limit]);

  if (!items) return null;

  let shown = items;
  if (query && (query.q || query.min || query.max || query.beds)) {
    const q = (query.q || "").toLowerCase().trim();
    shown = items.filter((l) => {
      const hay = `${l.address || ""} ${l.cityName || ""} ${l.zipcode || ""} ${l.subdivision || ""} ${l.countyName || ""}`.toLowerCase();
      if (q && !hay.includes(q)) return false;
      const price = Number(l.price) || 0;
      if (query.min && price < Number(query.min)) return false;
      if (query.max && price > Number(query.max)) return false;
      if (query.beds && Number(l.bedrooms) < Number(query.beds)) return false;
      return true;
    });
  }
  if (shown.length === 0) return emptyNode || null;

  return (
    <div data-testid={`idx-cards-${kind}`}>
      {heading && (
        <p className={`mb-8 font-mono text-[10px] uppercase tracking-[0.35em] ${dark ? "text-champagne" : "text-champagne"}`}>{heading}</p>
      )}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((l, i) => {
          const img = imgOf(l);
          const href = l.listingID ? `/listing/${l.listingID}` : "/listings";
          return (
            <Link
              key={l.listingID || i}
              to={href}
              data-testid={`listing-card-${l.listingID || i}`}
              className={`group block border transition-all duration-500 hover:-translate-y-1 ${
                dark
                  ? "border-white/15 bg-white/5 hover:border-champagne/50"
                  : "border-warm-border bg-white hover:shadow-[0_24px_60px_rgba(0,0,0,0.08)]"
              }`}
            >
              <div className="img-zoom relative">
                {img ? (
                  <img src={img} alt={`${l.address || "Property"}, ${l.cityName || ""}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                ) : (
                  <div className="aspect-[4/3] w-full bg-stone-beige" aria-hidden="true" />
                )}
                {l.propStatus && (
                  <span className="absolute left-0 top-4 bg-charcoal px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-ivory">
                    {l.propStatus}
                  </span>
                )}
              </div>
              <div className="p-6">
                <p className={`font-serif text-2xl font-light ${dark ? "text-champagne" : "text-charcoal"}`}>{l.listingPrice}</p>
                <p className={`mt-2 font-serif text-lg font-light leading-snug ${dark ? "text-ivory" : "text-charcoal"}`}>{l.address}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-muted">
                  {[l.cityName, l.stateAbrv, l.zipcode].filter(Boolean).join(", ")}
                </p>
                <div className={`mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4 ${dark ? "border-white/15" : "border-warm-border"}`}>
                  <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${dark ? "text-ivory/70" : "text-slate-muted"}`}>
                    <BedDouble size={13} strokeWidth={1.25} aria-hidden="true" /> {l.bedrooms ?? "—"} Beds
                  </span>
                  <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${dark ? "text-ivory/70" : "text-slate-muted"}`}>
                    <Bath size={13} strokeWidth={1.25} aria-hidden="true" /> {l.totalBaths ?? "—"} Baths
                  </span>
                  {l.sqFt && (
                    <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${dark ? "text-ivory/70" : "text-slate-muted"}`}>
                      <Ruler size={13} strokeWidth={1.25} aria-hidden="true" /> {l.sqFt} SqFt
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default ListingCards;
